// Global Instructions Rule Applied!
//
// Owners section — who on the team is responsible for this account, by cfg
// role (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md Phase 0). Standard kit:
// antd Table size='small' inside a FormSection (CommitmentsTab precedent),
// Add in headerActions opening an AntD Modal with the default footer, row
// actions per the word-button grammar (End = default bordered, Delete =
// icon-only text). An exclusive role (Primary RM) closes the previous holder
// server-side; end dates are exclusive (ending today takes effect now).
// Phase 8 (person identity): ownership is a contact relationship — a
// contact_accounts row under an account_ownership title — so the owner is
// the durable contact. The picker still offers team logins (the RPC resolves
// or creates the contact), and a holder whose login is gone renders by
// contact name with a "no login" tag.
import React, { useCallback, useMemo, useState } from 'react'
import { Button, Input, Modal, Popconfirm, Select, Switch, Table, Tag, Tooltip } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import type { ColumnsType } from 'antd/es/table'
import dayjs, { Dayjs } from 'dayjs'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { AccountOwnerRecord } from '../../../state_manager/data_models/relationships_model.ts'
import { AccountOwnerRoleModel } from '../../../state_manager/data_models/account_owner_role_model.ts'
import { useAccountOwners } from '../hooks/useAccountOwners.ts'
import { saveAccountOwner, endAccountOwner, deleteAccountOwner } from '../services/relationshipsService.ts'
import { searchSystemUsers } from '../../email/services/emailService.ts'

interface AccountOwnersSectionProps {
  accountId: string | null | undefined
  /** Registers the refetch with the host form's Refresh (design patterns: data freshness). */
  onRefreshReady?: (refreshFn: () => Promise<void>) => void
  className?: string
  title?: string
}

interface UserOption {
  value: string
  label: string
  email?: string
}

const fmtDate = (v?: string | null) => (v ? dayjs(v).format('MM/DD/YYYY') : '')

export function AccountOwnersSection({ accountId, onRefreshReady, className, title = 'Owners' }: AccountOwnersSectionProps) {
  const { owners, loading, refetch } = useAccountOwners(accountId)
  const roles = useSelector((state: any) => (state.globalConfigs?.account_owner_roles || []) as AccountOwnerRoleModel[])
  const [showHistory, setShowHistory] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [roleId, setRoleId] = useState<string | undefined>(undefined)
  const [userId, setUserId] = useState<string | undefined>(undefined)
  const [from, setFrom] = useState<Dayjs | null>(dayjs())
  const [notes, setNotes] = useState('')
  const [userOptions, setUserOptions] = useState<UserOption[]>([])
  const [searching, setSearching] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  React.useEffect(() => {
    if (onRefreshReady) onRefreshReady(refetch)
  }, [onRefreshReady, refetch])

  const rows = useMemo(() => (showHistory ? owners : owners.filter((o) => o.prog__is_current)), [owners, showHistory])
  const historyCount = owners.length - owners.filter((o) => o.prog__is_current).length

  const searchTimer = React.useRef<number | null>(null)
  const onSearchUsers = useCallback((term: string) => {
    if (searchTimer.current) window.clearTimeout(searchTimer.current)
    searchTimer.current = window.setTimeout(async () => {
      setSearching(true)
      try {
        const res: any = await searchSystemUsers(term, getAccessToken(store.getState().auth))
        const list: any[] = res?.data ?? res?.options ?? (Array.isArray(res) ? res : [])
        setUserOptions(
          list.map((u: any) => ({ value: String(u.value ?? u.id), label: String(u.label ?? u.name ?? u.email), email: u.email }))
        )
      } catch (e) {
        showErrorToast(e instanceof Error ? e.message : 'User search failed')
      } finally {
        setSearching(false)
      }
    }, 250)
  }, [])

  const openAdd = () => {
    setRoleId(roles.find((r) => r.role_key === 'primary_rm')?.id ?? roles[0]?.id)
    setUserId(undefined)
    setFrom(dayjs())
    setNotes('')
    setUserOptions([])
    onSearchUsers('')
    setModalOpen(true)
  }

  const submit = async () => {
    if (!accountId || !roleId || !userId) return
    setSubmitting(true)
    try {
      await saveAccountOwner(
        {
          account_id: accountId,
          system_user_id: userId,
          owner_role_id: roleId,
          from_date: from ? from.format('YYYY-MM-DD') : undefined,
          notes: notes.trim() || null
        },
        getAccessToken(store.getState().auth)
      )
      toast.success('Owner assigned')
      setModalOpen(false)
      await refetch()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to assign owner')
    } finally {
      setSubmitting(false)
    }
  }

  const endNow = async (row: AccountOwnerRecord) => {
    setBusyId(row.contact_accounts__id)
    try {
      await endAccountOwner(row.contact_accounts__id, dayjs().format('YYYY-MM-DD'), getAccessToken(store.getState().auth))
      toast.success('Assignment ended')
      await refetch()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to end assignment')
    } finally {
      setBusyId(null)
    }
  }

  const remove = async (row: AccountOwnerRecord) => {
    setBusyId(row.contact_accounts__id)
    try {
      await deleteAccountOwner(row.contact_accounts__id, getAccessToken(store.getState().auth))
      toast.success('Assignment deleted')
      await refetch()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to delete assignment')
    } finally {
      setBusyId(null)
    }
  }

  const columns: ColumnsType<AccountOwnerRecord> = [
    {
      title: 'Role',
      dataIndex: 'contact_relationship_titles__name',
      key: 'role',
      width: 150,
      render: (v: string, r) => (
        <span>
          {v}
          {r.contact_relationship_titles__is_exclusive ? (
            <Tooltip title='One current holder per account'>
              <Tag color='blue' style={{ marginLeft: 6 }}>exclusive</Tag>
            </Tooltip>
          ) : null}
        </span>
      )
    },
    {
      title: 'Team member',
      dataIndex: 'system_users__name',
      key: 'user',
      render: (v: string | null, r) => (
        <span className={r.prog__is_current ? '' : 'text-gray-400'}>
          {v || r.contacts__name || r.system_users__email || '-'}
          {r.system_users__email && (v || r.contacts__name) ? <span className='text-gray-400 text-xs ml-2'>{r.system_users__email}</span> : null}
          {!r.system_users__id ? (
            <Tooltip title='This person has no login — history is kept, nothing can be assigned to them'>
              <Tag style={{ marginLeft: 6 }}>no login</Tag>
            </Tooltip>
          ) : null}
        </span>
      )
    },
    { title: 'From', dataIndex: 'contact_accounts__from_date', key: 'from', width: 110, render: fmtDate },
    {
      title: 'To',
      dataIndex: 'contact_accounts__to_date',
      key: 'to',
      width: 110,
      render: (v: string | null, r) => (r.prog__is_current ? <Tag color='green'>current</Tag> : fmtDate(v) || <Tag>ended</Tag>)
    },
    { title: 'Notes', dataIndex: 'contact_accounts__notes', key: 'notes', ellipsis: true, render: (v: string | null) => v || '' },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_: unknown, r) => (
        <span className='inline-flex items-center gap-1'>
          {r.prog__is_current ? (
            <Button size='small' loading={busyId === r.contact_accounts__id} onClick={() => endNow(r)}>
              End
            </Button>
          ) : null}
          <Popconfirm title='Delete this assignment?' okText='Delete' onConfirm={() => remove(r)}>
            <Button type='text' danger size='small' icon={<FontAwesomeIcon icon={faTrash} />} loading={busyId === r.contact_accounts__id} />
          </Popconfirm>
        </span>
      )
    }
  ]

  const roleOptions = roles.map((r) => ({ value: r.id, label: r.label }))

  return (
    <div data-component='AccountOwnersSection' className={className}>
      <FormSection
        title={title}
        titleColor='blue'
        loading={loading && owners.length === 0}
        headerActions={
          <span className='inline-flex items-center gap-3'>
            {historyCount > 0 ? (
              <span className='inline-flex items-center gap-1 text-xs text-gray-600'>
                <Switch size='small' checked={showHistory} onChange={setShowHistory} /> Show history
              </span>
            ) : null}
            <Button size='small' icon={<FontAwesomeIcon icon={faPlus} />} onClick={openAdd} disabled={!accountId}>
              Add
            </Button>
          </span>
        }
      >
        <Table<AccountOwnerRecord>
          rowKey='contact_accounts__id'
          size='small'
          pagination={false}
          columns={columns}
          dataSource={rows}
          loading={loading}
          locale={{ emptyText: <div className='text-center text-gray-500 text-sm py-2'>No owner assigned</div> }}
        />
      </FormSection>

      <Modal
        title='Assign owner'
        open={modalOpen}
        okText='Assign'
        okButtonProps={{ disabled: !roleId || !userId }}
        confirmLoading={submitting}
        onOk={submit}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
      >
        <div className='flex flex-col gap-3 pt-2'>
          <div>
            <div className='text-xs text-gray-600 mb-1'>Role</div>
            <Select className='w-full' options={roleOptions} value={roleId} onChange={(v) => setRoleId(v)} />
          </div>
          <div>
            <div className='text-xs text-gray-600 mb-1'>Team member</div>
            <Select
              className='w-full'
              showSearch
              filterOption={false}
              loading={searching}
              placeholder='Search by name or email'
              options={userOptions}
              value={userId}
              onSearch={onSearchUsers}
              onChange={(v) => setUserId(v)}
              notFoundContent={searching ? 'Searching…' : 'No match'}
            />
          </div>
          <div>
            <div className='text-xs text-gray-600 mb-1'>Effective from</div>
            <DatePicker className='w-full' value={from} onChange={(d) => setFrom(d)} format='MM/DD/YYYY' />
          </div>
          <div>
            <div className='text-xs text-gray-600 mb-1'>Notes</div>
            <Input.TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} />
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default AccountOwnersSection
